import React from 'react';
import { X } from 'lucide-react';
import './EmailPopup.css';

interface Email {
  subject: string;
  body: string;
  bodyPreview: string;
  to: string;
  sentDateTime: string;
}

interface EmailPopupProps {
  isOpen: boolean;
  onClose: () => void;
  emails: Email[];
  loading: boolean;
  error: string | null;
  notConnected: boolean;
  onConnect: () => void;
}

const EmailPopup: React.FC<EmailPopupProps> = ({
  isOpen,
  onClose,
  emails,
  loading,
  error,
  notConnected,
  onConnect,
}) => {
  if (!isOpen) return null;

  // Strip HTML tags from email body to get plain text
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Format date to readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="email-popup-overlay" onClick={onClose}>
      <div className="email-popup-container" onClick={(e) => e.stopPropagation()}>
        <div className="email-popup-header">
          <h2>Recent Sent Emails (Last 5 Minutes)</h2>
          <button className="email-popup-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="email-popup-content">
          {loading && (
            <div className="email-popup-loading">
              <div className="loading-spinner"></div>
              <p>Loading emails...</p>
            </div>
          )}

          {notConnected && !loading && (
            <div className="email-popup-not-connected">
              <p>Outlook not connected</p>
              <button className="email-connect-button" onClick={onConnect}>
                Connect to Outlook
              </button>
            </div>
          )}

          {error && !notConnected && !loading && (
            <div className="email-popup-error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && !notConnected && emails.length === 0 && (
            <div className="email-popup-empty">
              <p>No emails sent in the last 5 minutes</p>
            </div>
          )}

          {!loading && !error && !notConnected && emails.length > 0 && (
            <div className="email-list">
              {emails.map((email, index) => (
                <div key={index} className="email-item">
                  <div className="email-header">
                    <span className="email-subject">{email.subject || '(No Subject)'}</span>
                    <span className="email-date">{formatDate(email.sentDateTime)}</span>
                  </div>
                  <div className="email-to">
                    <span className="email-to-label">To:</span> {email.to}
                  </div>
                  <div className="email-body">
                    {stripHtml(email.body || email.bodyPreview).substring(0, 200)}
                    {stripHtml(email.body || email.bodyPreview).length > 200 && '...'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailPopup;
